import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { 
  Plus, 
  CircleNotch,
  Trash,
  CheckCircle,
  Calendar,
  CalendarBlank,
  Clock,
  CaretLeft,
  CaretRight,
  Bell,
  CheckSquare,
  Warning,
  Money,
  Receipt,
  ShieldCheck,
  Handshake,
  PencilSimple
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPES = [
  { value: 'task', label: 'Task', color: '#3b82f6', icon: CheckSquare },
  { value: 'reminder', label: 'Reminder', color: '#8b5cf6', icon: Bell },
  { value: 'meeting', label: 'Meeting', color: '#06b6d4', icon: Handshake },
  { value: 'followup', label: 'Follow-up', color: '#f59e0b', icon: Clock },
  { value: 'payable', label: 'Payable', color: '#ef4444', icon: Money },
  { value: 'receivable', label: 'Receivable', color: '#22c55e', icon: Receipt },
  { value: 'pdc', label: 'PDC', color: '#ec4899', icon: Money },
  { value: 'compliance', label: 'Compliance', color: '#f97316', icon: ShieldCheck },
  { value: 'other', label: 'Other', color: '#6b7280', icon: Calendar },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'bg-slate-500/10 text-slate-600 border-slate-500/20' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  { value: 'high', label: 'High', color: 'bg-red-500/10 text-red-600 border-red-500/20' },
];

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function CalendarPage() {
  const { user, hasPermission } = useAuth();
  const { formatCurrency } = useCompany();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month'); // month, week, list
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'task',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    start_time: '',
    end_time: '',
    all_day: true,
    priority: 'medium',
    reminder_before: 30
  });

  const canCreate = hasPermission('hr', 'create');
  const canEdit = hasPermission('hr', 'edit');
  const canDelete = hasPermission('hr', 'delete');

  useEffect(() => {
    fetchData();
  }, [currentDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const [eventsRes, statsRes, upcomingRes] = await Promise.all([
        axios.get(`${API_URL}/api/calendar/events`, {
          params: {
            start_date: startOfMonth.toISOString().split('T')[0],
            end_date: endOfMonth.toISOString().split('T')[0],
            include_system: true
          }
        }),
        axios.get(`${API_URL}/api/calendar/stats`),
        axios.get(`${API_URL}/api/calendar/upcoming`, { params: { days: 14 } })
      ]);
      
      setEvents(eventsRes.data);
      setStats(statsRes.data);
      setUpcomingEvents(upcomingRes.data);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.start_date) {
      toast.error('Title and start date are required');
      return;
    }

    setFormLoading(true);
    try {
      if (editEvent) {
        await axios.patch(`${API_URL}/api/calendar/events/${editEvent.id}`, formData);
        toast.success('Event updated');
      } else {
        await axios.post(`${API_URL}/api/calendar/events`, formData);
        toast.success('Event created');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save event');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/calendar/events/${id}`);
      toast.success('Event deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleComplete = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/calendar/events/${id}/complete`);
      toast.success('Event marked as completed');
      fetchData();
    } catch (error) {
      toast.error('Failed to complete event');
    }
  };

  const handleEdit = (event) => {
    if (event.is_system) {
      toast.info('System events cannot be edited');
      return;
    }
    setEditEvent(event);
    setFormData({
      title: event.title,
      description: event.description || '',
      event_type: event.event_type,
      start_date: event.start_date,
      end_date: event.end_date || '',
      start_time: event.start_time || '',
      end_time: event.end_time || '',
      all_day: event.all_day !== false,
      priority: event.priority || 'medium',
      reminder_before: event.reminder_before || 30
    });
    setDialogOpen(true);
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setFormData(prev => ({
      ...prev,
      start_date: date.toISOString().split('T')[0]
    }));
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditEvent(null);
    setSelectedDate(null);
    setFormData({
      title: '',
      description: '',
      event_type: 'task',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      start_time: '',
      end_time: '',
      all_day: true,
      priority: 'medium',
      reminder_before: 30
    });
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({
        day: prevMonthLastDay - i,
        currentMonth: false,
        date: new Date(year, month - 1, prevMonthLastDay - i)
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        currentMonth: true,
        date: new Date(year, month, i)
      });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        currentMonth: false,
        date: new Date(year, month + 1, i)
      });
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(e => e.start_date === dateStr);
  };

  const getEventTypeConfig = (type) => {
    return EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[EVENT_TYPES.length - 1];
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <CalendarBlank size={28} weight="duotone" className="text-primary" />
            Calendar & Reminders
          </h1>
          <p className="text-muted-foreground mt-1">Track tasks, deadlines, and important dates</p>
        </div>
        {canCreate && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-event-btn">
                <Plus size={16} className="mr-2" />
                Add Event
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editEvent ? 'Edit Event' : 'Add Event'}</DialogTitle>
                <DialogDescription>
                  Create tasks, reminders, and calendar events
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Title *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
                      placeholder="Event title"
                      data-testid="event-title"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={formData.event_type} onValueChange={(v) => setFormData(p => ({ ...p, event_type: v }))}>
                      <SelectTrigger data-testid="event-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EVENT_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: type.color }} />
                              {type.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select value={formData.priority} onValueChange={(v) => setFormData(p => ({ ...p, priority: v }))}>
                      <SelectTrigger data-testid="event-priority">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Start Date *</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData(p => ({ ...p, start_date: e.target.value }))}
                      data-testid="event-start-date"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData(p => ({ ...p, end_date: e.target.value }))}
                      data-testid="event-end-date"
                    />
                  </div>
                  
                  {!formData.all_day && (
                    <>
                      <div className="space-y-2">
                        <Label>Start Time</Label>
                        <Input
                          type="time"
                          value={formData.start_time}
                          onChange={(e) => setFormData(p => ({ ...p, start_time: e.target.value }))}
                          data-testid="event-start-time"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>End Time</Label>
                        <Input
                          type="time"
                          value={formData.end_time}
                          onChange={(e) => setFormData(p => ({ ...p, end_time: e.target.value }))}
                          data-testid="event-end-time"
                        />
                      </div>
                    </>
                  )}
                  
                  <div className="col-span-2 flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="all_day"
                      checked={formData.all_day}
                      onChange={(e) => setFormData(p => ({ ...p, all_day: e.target.checked }))}
                      className="rounded border-input"
                    />
                    <Label htmlFor="all_day" className="cursor-pointer">All day event</Label>
                  </div>
                  
                  <div className="col-span-2 space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                      placeholder="Event details..."
                      rows={2}
                      data-testid="event-description"
                    />
                  </div>
                </div>
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={formLoading} data-testid="save-event-btn">
                    {formLoading && <CircleNotch className="animate-spin mr-2" size={16} />}
                    {editEvent ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-tactile rounded-lg p-4"
        >
          <p className="text-xs text-muted-foreground">This Week</p>
          <p className="text-xl font-bold mt-1">{stats?.this_week || 0}</p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="card-tactile rounded-lg p-4"
        >
          <p className="text-xs text-muted-foreground">This Month</p>
          <p className="text-xl font-bold mt-1">{stats?.this_month || 0}</p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card-tactile rounded-lg p-4"
        >
          <p className="text-xs text-muted-foreground">Pending</p>
          <p className="text-xl font-bold text-amber-600 mt-1">{stats?.pending || 0}</p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="card-tactile rounded-lg p-4"
        >
          <p className="text-xs text-muted-foreground">Overdue</p>
          <p className="text-xl font-bold text-red-600 mt-1">{stats?.overdue || 0}</p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-tactile rounded-lg p-4"
        >
          <p className="text-xs text-muted-foreground">Completed</p>
          <p className="text-xl font-bold text-emerald-600 mt-1">{stats?.completed || 0}</p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="card-tactile rounded-lg p-4"
        >
          <p className="text-xs text-muted-foreground">System Events</p>
          <p className="text-xl font-bold text-blue-600 mt-1">{stats?.system_events_upcoming || 0}</p>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="lg:col-span-3 card-tactile rounded-lg overflow-hidden"
        >
          {/* Calendar Header */}
          <div className="p-4 border-b border-border bg-muted/30 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="icon" onClick={() => navigateMonth(-1)}>
                <CaretLeft size={16} />
              </Button>
              <h2 className="text-lg font-semibold">
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <Button variant="outline" size="icon" onClick={() => navigateMonth(1)}>
                <CaretRight size={16} />
              </Button>
            </div>
            <Button variant="outline" onClick={() => setCurrentDate(new Date())}>
              Today
            </Button>
          </div>
          
          {/* Calendar Grid */}
          <div className="p-4">
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {DAYS.map(day => (
                <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1">
              {getCalendarDays().map((dayObj, idx) => {
                const dayEvents = getEventsForDate(dayObj.date);
                const isCurrentDay = isToday(dayObj.date);
                
                return (
                  <div
                    key={idx}
                    className={`min-h-24 p-1 border rounded-lg cursor-pointer transition-colors ${
                      dayObj.currentMonth 
                        ? 'bg-card hover:bg-muted/50' 
                        : 'bg-muted/30 text-muted-foreground'
                    } ${isCurrentDay ? 'ring-2 ring-primary' : 'border-border'}`}
                    onClick={() => {
                      if (canCreate) {
                        handleDateClick(dayObj.date);
                      } else {
                        toast.info('You need HR create permission to add an event. Ask your admin to grant it.');
                      }
                    }}
                    data-testid={`calendar-day-${dayObj.date.toISOString().split('T')[0]}`}
                  >
                    <div className={`text-sm font-medium mb-1 ${isCurrentDay ? 'text-primary' : ''}`}>
                      {dayObj.day}
                    </div>
                    <div className="space-y-0.5">
                      {dayEvents.slice(0, 3).map((event, i) => {
                        const config = getEventTypeConfig(event.event_type);
                        return (
                          <div
                            key={i}
                            className="text-xs px-1 py-0.5 rounded truncate"
                            style={{ backgroundColor: `${config.color}20`, color: config.color }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(event);
                            }}
                          >
                            {event.title}
                          </div>
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <div className="text-xs text-muted-foreground px-1">
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Legend */}
          <div className="p-4 border-t border-border bg-muted/30">
            <div className="flex flex-wrap gap-3">
              {EVENT_TYPES.map(type => (
                <div key={type.value} className="flex items-center gap-1.5 text-xs">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: type.color }} />
                  <span className="text-muted-foreground">{type.label}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Upcoming Events Sidebar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card-tactile rounded-lg overflow-hidden"
        >
          <div className="p-4 border-b border-border bg-muted/30">
            <h3 className="font-semibold flex items-center gap-2">
              <Clock size={18} className="text-primary" />
              Upcoming (14 days)
            </h3>
          </div>
          
          <div className="p-2 max-h-[500px] overflow-y-auto">
            {upcomingEvents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No upcoming events
              </div>
            ) : (
              <div className="space-y-1">
                {upcomingEvents.map(event => {
                  const config = getEventTypeConfig(event.event_type);
                  const EventIcon = config.icon;
                  const priorityConfig = PRIORITY_OPTIONS.find(p => p.value === event.priority);
                  
                  return (
                    <div
                      key={event.id}
                      className="p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer group"
                      onClick={() => {
                        if (event.is_system) {
                          toast.info(`System event (from ${(event.event_type || 'source').replace(/_/g, ' ')}). Open the source document to edit it.`);
                        } else {
                          handleEdit(event);
                        }
                      }}
                      data-testid={`upcoming-event-${event.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <div 
                          className="p-1.5 rounded-md mt-0.5"
                          style={{ backgroundColor: `${config.color}20` }}
                        >
                          <EventIcon size={14} style={{ color: config.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{event.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-muted-foreground">{event.start_date}</span>
                            {event.is_system && (
                              <Badge variant="outline" className="text-[10px] h-4">System</Badge>
                            )}
                          </div>
                        </div>
                        {!event.is_system && canEdit && (
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleComplete(event.id);
                              }}
                            >
                              <CheckCircle size={14} className="text-emerald-600" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
